/**
 * Name the document after the screen the trainer is looking at.
 *
 * Every route in this app rendered as "MoveBuddy" — the one static `<title>` in `index.html` —
 * so a trainer with the library and a client's plan open in two tabs could not tell them apart,
 * a bookmark recorded nothing, and browser history was a column of identical rows. On a desktop
 * tool where the whole point is having several things open, that is a functional gap rather than
 * a polish item.
 *
 * ── Why a hook and not the router's `head` option ────────────────────────────────────────────
 * `@tanstack/react-router` 1.170.18 does carry a route-level `head` (verified in
 * `node_modules/@tanstack/router-core/dist/esm/route.d.ts`), and it is the wrong tool twice
 * over. It is evaluated outside React, so `t` is not in scope and a language change would not
 * re-run it — this app switches language at runtime. And it is evaluated when the match is
 * created, so `/plans/$planId` could not name itself after a plan whose name arrives later.
 * Both are the normal case here, so the effect is the mechanism and `head` is left for a route
 * that genuinely has its title in its loader.
 *
 * ── The brand suffix is interpolated, not concatenated ───────────────────────────────────────
 * `web:app.documentTitle` owns the whole format. The separator is a decision — KANON uses the
 * middle dot throughout — and a `${page} · MoveBuddy` template literal at three call sites is
 * three places for it to drift, plus a user-facing string outside `t()`.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The document's own title, captured before any screen has renamed it.
 *
 * Read at module evaluation, which is before the first render: the screens that call this hook
 * are what import it. It is restored on unmount so signing out of `/plans` does not leave the
 * sign-in screen calling itself "Programy · MoveBuddy" — the auth screens do not name themselves
 * and there is nothing else to put the title back.
 */
const APP_TITLE = document.title;

/**
 * Set `document.title` to this screen's name for as long as the screen is mounted.
 *
 * @param page The screen's own name, already translated — normally the same string as its `h1`.
 */
export function useDocumentTitle(page: string): void {
  const { t } = useTranslation();
  const title = t('web:app.documentTitle', { page });

  useEffect(() => {
    document.title = title;
    return () => {
      document.title = APP_TITLE;
    };
  }, [title]);
}
